import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Mail, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import usePushNotifications from '@/hooks/usePushNotifications';

const NotificationPrompt = ({ user }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const { isSupported, isSubscribed, subscribe } = usePushNotifications();
  
  useEffect(() => {
    // Show prompt if user is logged in, hasn't dismissed it, and hasn't subscribed to push
    if (user && isSupported && !isSubscribed) {
      const dismissed = localStorage.getItem('push-prompt-dismissed-v2');
      const sessionDismissed = sessionStorage.getItem('push-prompt-session-dismissed');
      const lastShown = localStorage.getItem('push-prompt-last-shown-v2');
      const now = Date.now();

      // Show if never dismissed permanently, not dismissed this session,
      // and either never shown OR not shown in the last 7 days.
      if (!dismissed && !sessionDismissed && (!lastShown || now - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000)) {
        // Delay showing by 3 seconds so user settles in
        const timer = setTimeout(() => {
          setShowPrompt(true);
          localStorage.setItem('push-prompt-last-shown-v2', now.toString());
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, isSupported, isSubscribed]);

  const handleEnable = async () => {
    try {
      await subscribe();
      setShowPrompt(false);
      // Send a test push to confirm it works
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const axios = (await import('axios')).default;
          await axios.post('/api/push/test', {}, { headers: { Authorization: `Bearer ${token}` } });
        }
      } catch {}
    } catch (error) {
      console.error('Failed to enable notifications');
    }
  };

  const handleDismiss = () => {
    // Sticky for the rest of this session so the banner doesn't keep
    // popping back after every page navigation and overlap UI like the
    // poll Submit bar at bottom-right.
    sessionStorage.setItem('push-prompt-session-dismissed', '1');
    setShowPrompt(false);
  };

  const handleNeverShow = () => {
    localStorage.setItem('push-prompt-dismissed-v2', 'true');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:top-20 md:bottom-auto md:right-4 md:w-96 z-50"
      >
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-primary/20 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-accent p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                <span className="font-bold">Stay Connected!</span>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-600">
              Enable notifications to know instantly when:
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Smartphone className="w-4 h-4 text-primary" />
                </div>
                <span>Someone wants to connect with you</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Bell className="w-4 h-4 text-primary" />
                </div>
                <span>People RSVP to your events</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <span>You receive new messages</span>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
              <strong>Tip:</strong> You'll also receive email notifications automatically. Check your inbox!
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleEnable}
                className="flex-1 bg-primary hover:bg-primary/90 rounded-full font-bold"
                data-testid="enable-notifications-btn"
              >
                Enable Notifications
              </Button>
            </div>
            
            <button
              onClick={handleNeverShow}
              className="w-full text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Don't show this again
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPrompt;
